import React, { useEffect, useMemo, useState } from 'react';
import { SearchIcon, DollarSignIcon, UserIcon, BadgePercentIcon, Building2Icon } from 'lucide-react';
export const Features = () => {
  // ---- Parámetros y estados ----
  const [propertyValueUF, setPropertyValueUF] = useState<number>(25000); // ✅ Parte en 25.000 UF
  const [ufToCLP, setUfToCLP] = useState<number>(40000); // UF en CLP (editable/auto)
  const [ufDate, setUfDate] = useState<string>('');
  const [ufLoading, setUfLoading] = useState<boolean>(false);
  const [ufError, setUfError] = useState<string | null>(null);
  // Política comercial (ajusta aquí)
  const COMMISSION_RATE = 0.02; // 2% tradicional
  const FEE_CLP_BASE = 10000000; // tarifa fija base (CLP)
  const IVA_RATE = 0.19; // IVA Chile
  // Cálculos
  const commissionUF = useMemo(() => propertyValueUF * COMMISSION_RATE, [propertyValueUF]);
  const commissionCLP = useMemo(() => Math.round(commissionUF * ufToCLP), [commissionUF, ufToCLP]);
  const feeCLPConIVA = useMemo(() => Math.round(FEE_CLP_BASE * (1 + IVA_RATE)), []);
  const ahorroCLP = useMemo(() => Math.max(0, commissionCLP - feeCLPConIVA), [commissionCLP, feeCLPConIVA]);
  // UF auto (mindicador.cl)
  useEffect(() => {
    let mounted = true;
    const loadUF = async () => {
      try {
        setUfLoading(true);
        setUfError(null);
        const res = await fetch('https://mindicador.cl/api/uf');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Compatibilidad de estructuras
        const valor = (data?.uf && typeof data.uf.valor === 'number' ? data.uf.valor : null) ?? (typeof data?.serie?.[0]?.valor === 'number' ? data.serie[0].valor : null);
        const fecha = (data?.uf && typeof data.uf.fecha === 'string' ? data.uf.fecha : null) ?? (typeof data?.serie?.[0]?.fecha === 'string' ? data.serie[0].fecha : null);
        if (mounted) {
          if (typeof valor === 'number' && !Number.isNaN(valor)) {
            setUfToCLP(Math.round(valor));
          } else {
            throw new Error('Estructura inesperada en respuesta UF');
          }
          if (fecha) {
            const d = new Date(fecha);
            const formatted = d.toLocaleDateString('es-CL', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit'
            });
            setUfDate(formatted);
          }
        }
      } catch (_err) {
        if (mounted) setUfError('No pudimos cargar la UF automáticamente. Puedes ingresarla manualmente.');
      } finally {
        if (mounted) setUfLoading(false);
      }
    };
    loadUF();
    return () => {
      mounted = false;
    };
  }, []);
  // Pasos clave para VENDER
  const steps = [{
    icon: Building2Icon,
    title: 'Un solo corredor, abiertos a canje',
    desc: 'Lideramos tu venta y aprovechamos la red completa de corredores para maximizar visibilidad.'
  }, {
    icon: SearchIcon,
    title: 'Filtro profundo de interesados',
    desc: 'Menos visitas, más reales. Priorizamos a quienes tienen capacidad y timing de compra.'
  }, {
    icon: DollarSignIcon,
    title: 'Tarifa fija y transparente',
    desc: `Tarifa fija en CLP (con IVA), sin comisión variable ni cargo al comprador.`
  }, {
    icon: UserIcon,
    title: 'Acompañamiento integral',
    desc: 'Carpeta legal y municipal, coordinación, negociación y cierre.'
  }, {
    icon: BadgePercentIcon,
    title: 'Marketing completo',
    desc: 'Fotos profesionales, tour virtual y planos que venden por sí solos.'
  }];
  return <section id="features" className="py-32 bg-white" data-id="element-64">
      {/* Encabezado */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-65">
        <h2 className="text-base font-semibold text-blue-600 tracking-wide uppercase" data-id="element-66">
          Así funciona vender con MINGA
        </h2>
        <p className="mt-2 text-4xl font-extrabold text-gray-900 sm:text-5xl leading-tight" data-id="element-67">
          Vende inteligente,{' '}
          <span className="whitespace-nowrap bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent" data-id="element-68">
            con tarifa fija
          </span>
        </p>
        <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto" data-id="element-69">
          Lideramos la venta con foco, datos y una red activa. Sin comisión al
          comprador. Sin sorpresas.
        </p>
      </div>
      {/* Pasos con scroll horizontal en mobile */}
      <div className="mt-20 px-4 sm:px-6 lg:px-8" data-id="element-70">
        <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pt-4 pl-4" data-id="element-71">
          <div className="flex gap-6 sm:grid sm:grid-cols-3 sm:gap-12 max-w-7xl mx-auto min-w-max sm:min-w-0" data-id="element-72">
            {steps.map((step, index) => {
            const Icon = step.icon as any;
            return <div key={index} className="bg-white rounded-lg shadow-sm p-6 text-left border border-gray-100 relative flex-shrink-0 w-80 sm:w-auto" data-id="element-73">
                  <div className="absolute -top-3 -left-3 bg-blue-600 text-white w-8 h-8 flex items-center justify-center text-sm font-bold rounded-full shadow" data-id="element-74">
                    {index + 1}
                  </div>
                  <Icon className="h-6 w-6 text-blue-600 mb-4" data-id="element-75" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-76">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-base text-gray-500" data-id="element-77">{step.desc}</p>
                </div>;
          })}
          </div>
        </div>
        {/* Indicador de scroll en mobile */}
        <div className="mt-4 text-center sm:hidden" data-id="element-78">
          <p className="text-xs text-gray-400" data-id="element-79">← Desliza para ver más →</p>
        </div>
      </div>
      {/* Calculadora de ahorro (misma estética, más ancho) */}
      <div className="mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-80">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8" data-id="element-81">
          <h3 className="text-xl font-semibold text-gray-900 text-center" data-id="element-82">
            ¿Cuánto ahorrarías frente a una comisión del 2%?
          </h3>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-83">
            {/* Slider UF propiedad */}
            <div className="col-span-1 sm:col-span-2" data-id="element-84">
              <label className="block text-sm text-gray-600 mb-1" data-id="element-85">
                Precio de tu propiedad (UF)
              </label>
              <input type="range" min={3000} max={60000} step={500} value={propertyValueUF} onChange={e => setPropertyValueUF(Number(e.target.value))} className="w-full accent-blue-600" aria-label="UF de la propiedad" data-id="element-86" />
              <div className="mt-2 text-gray-800 font-medium" data-id="element-87">
                {propertyValueUF.toLocaleString('es-CL')} UF
              </div>
            </div>

            {/* UF manual / auto */}
            <div data-id="element-88">
              <label className="block text-sm text-gray-600 mb-1" data-id="element-89">
                Valor UF (CLP){' '}
                {ufLoading ? <span className="text-blue-600" data-id="element-90">(cargando…)</span> : null}
              </label>
              <input type="number" min={1} step={100} value={ufToCLP} onChange={e => setUfToCLP(Number(e.target.value || 0))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-91" />
              {ufError && <p className="mt-1 text-xs text-amber-600" data-id="element-92">{ufError}</p>}
              {!ufError && !ufLoading && ufDate && <p className="mt-1 text-xs text-gray-500" data-id="element-93">
                  UF vigente al {ufDate}
                </p>}
            </div>
          </div>

          {/* Resultados */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center" data-id="element-94">
            <div className="rounded-xl border border-gray-200 p-5 bg-white" data-id="element-95">
              <div className="text-sm text-gray-600 mb-1" data-id="element-96">
                Comisión tradicional (2%)
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-97">
                {commissionCLP.toLocaleString('es-CL')} CLP
              </div>
              <div className="text-sm text-gray-500" data-id="element-98">
                {Math.round(commissionUF).toLocaleString('es-CL')} UF
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-5 bg-white" data-id="element-99">
              <div className="text-sm text-gray-600 mb-1" data-id="element-100">
                Tarifa MINGA (con IVA)
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-101">
                {feeCLPConIVA.toLocaleString('es-CL')} CLP
              </div>
              <div className="text-sm text-gray-500" data-id="element-102">
                Base: {FEE_CLP_BASE.toLocaleString('es-CL')} CLP + IVA
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 p-5 bg-blue-50" data-id="element-103">
              <div className="text-sm text-blue-700 mb-1 font-medium" data-id="element-104">
                Ahorro estimado
              </div>
              <div className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent" data-id="element-105">
                {ahorroCLP.toLocaleString('es-CL')} CLP
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500 text-center" data-id="element-106">
            * Cálculo: (UF propiedad × 2% × CLP/UF) − Tarifa fija (con IVA).
          </p>
        </div>
      </div>
    </section>;
};